import { get } from 'svelte/store';
import { getComponentQuery, getComponentQueries, getHistoryAll, getMacroHistory, getComponentHistory, deleteComponentQuery, testQuery } from './api';
import { queries, queryResults, history, macroHistory } from './stores.js';
import { numberWithDigitGrouping, weekFormatter } from './utils.js';
import AnchorCell from './components/AnchorCell.svelte';

export async function loadAll() {
  await loadQueries();
  await loadHistory();
  await loadMacroHistory();
}

export async function loadQueries() {
  const response = await getComponentQueries();
  if (response.ok) {
    let data = response.data;
    data.sort((a, b) => a.Name.localeCompare(b.Name));
    queries.set(data);
  } else {
    if (response.error) throw Error(response.error);
    else throw Error(response.status);
  }
}

export async function getQuery(id) {
  const response = await getComponentQuery(id);
  if (response.ok) {
    return response.data;
  } else {
    if (response.error) throw Error(response.error);
    else throw Error(response.status);
  }
}

export async function loadHistory() {
  const response = await getHistoryAll();
  if (response.ok) {
    history.set(response.data);
  } else {
    if (response.error) throw Error(response.error);
    else throw Error(response.status);
  }
}

export async function loadMacroHistory() {
  const response = await getMacroHistory(20);
  if (response.ok) {
    let data = response.data;
    data.reverse();
    macroHistory.set(data);
  } else {
    if (response.error) throw Error(response.error);
    else throw Error(response.status);
  }
}

export async function getHistory(id, n) {
  const response = await getComponentHistory(id, n);
  if (response.ok) {
    let data = await response.data;
    return data;
  } else {
    if (response.error) throw Error(response.error);
    else throw Error(response.status);
  }
}

export async function runQuery(id) {
  // Query results are cached.
  // Querying again is only done when cached result is an hour or older.
  // When query has changed the cache will though be clear.
  let results = get(queryResults);
  if (results[id]) {
    const age = Date.now() - results[id + "_date"];
    if (age < 60 * 60 * 1000) // age is less than an hour
      return results[id];
  }

  let q = get(queries);
  let query = q.find(x => x.PartitionKey === id);
  if (!query) query = await getQuery(id);
  const response = await testQuery(query);
  if (response.ok) {
    let data = response.data
    results[id] = data;
    results[id + "_date"] = Date.now();
    queryResults.set(results);
    return data;
  } else {
    if (response.error) throw Error(response.error);
    else throw Error(response.status);
  }
}

export async function deleteQuery(id) {
  const response = await deleteComponentQuery(id);
  if (!response.ok) {
    if (response.error) throw Error(response.error);
    else throw Error(response.status);
  }
}

export function getQueryColumns() {
  return [
    { name: "MPN", value: x => x.ManufacturerProductNumber, component: AnchorCell, props: x => ({ href: x.ProductUrl })},
    { name: "Datasheet", value: x => x.DatasheetUrl ? "Datasheet" : "", component: AnchorCell, props: x => ({ href: x.DatasheetUrl })},
    { name: "Description", value: x => x.Description.DetailedDescription || x.Description.ProductDescription },
    { name: "Stock", value: x => x.QuantityAvailable, formatter: x => numberWithDigitGrouping(x) },
    { name: "Unit Price", value: x => "$" + x.UnitPrice},
    { name: "Lead Time", value: x => x.ManufacturerLeadWeeks != null ? x.ManufacturerLeadWeeks * 7 : null, formatter: x => weekFormatter(x) },
    { name: "Status", value: x => x.ProductStatus.Status },
    { name: "RoHS", value: x => x.Classifications?.RohsStatus || "" }
  ];
}

export function getTotalMarketAvailability(parts) {
  if (!parts) return 0;
  return parts.reduce((sum, part) => sum + part.QuantityAvailable, 0);
}

export function getLifecycleStatusCount(lifecycleStatus) {
  if (!lifecycleStatus) return undefined;

  const good = (lifecycleStatus["Active"] || 0);
  const suspect = (lifecycleStatus["NRND"] || 0) + (lifecycleStatus["EOL"] || 0);
  const bad = lifecycleStatus["Obsolete"] + lifecycleStatus["DiscontinuedAtDigiKey"] || 0;
  const unknown = lifecycleStatus["Unknown"] || 0;
  return { good, suspect, bad, unknown };
}

export function getComplianceStatusCount(rohs) {
  if (!rohs) return undefined;

  const good = (rohs["Compliant"] || 0);
  const bad = (rohs["NonCompliant"] || 0);
  const unknown = rohs["Unknown"] || 0;
  return { good, suspect: 0, bad, unknown };
}